import axios from "axios";
import { useEffect, useState } from "react";
import { Clock } from "react-bootstrap-icons";

import { ApiConnectionString } from "../../../constants/ApiConntection";
import LoadingSpinner from "../../../components/LoadingSpinner";

import SettingsCard from "./SettingsCard";
import SectionMessage from "./SectionMessage";

import {
  createEmptyMessage,
  getErrorMessage,
} from "../settingsUtils";

const emptyBookingRules = {
  daysInAdvance: "",
  minimumNoticeRequired: "",
};

export default function BookingRulesSettings({ token }) {
  const [bookingRules, setBookingRules] =
    useState(emptyBookingRules);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [message, setMessage] =
    useState(createEmptyMessage);

  useEffect(() => {
    const getBookingRules = async () => {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setMessage(createEmptyMessage());

        const response = await axios.get(
          `${ApiConnectionString}/api/bookingRules`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = response.data || {};

        setBookingRules({
          daysInAdvance:
            data.daysInAdvance ??
            data.DaysInAdvance ??
            "",
          minimumNoticeRequired:
            data.minimumNoticeRequired ??
            data.MinimumNoticeRequired ??
            "",
        });
      } catch (error) {
        console.error(error);

        setMessage({
          error: getErrorMessage(
            error,
            "Failed to load booking rules."
          ),
          success: "",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getBookingRules();
  }, [token]);

  const updateField = (field, value) => {
    setBookingRules((current) => ({
      ...current,
      [field]: value,
    }));

    setMessage(createEmptyMessage());
  };

  const saveBookingRules = async () => {
    const daysInAdvance = Number(
      bookingRules.daysInAdvance
    );

    const minimumNoticeRequired = Number(
      bookingRules.minimumNoticeRequired
    );

    if (
      bookingRules.daysInAdvance === "" ||
      !Number.isFinite(daysInAdvance) ||
      daysInAdvance < 1
    ) {
      setMessage({
        error:
          "Days in advance must be at least 1 day.",
        success: "",
      });

      return;
    }

    if (
      bookingRules.minimumNoticeRequired === "" ||
      !Number.isFinite(minimumNoticeRequired) ||
      minimumNoticeRequired < 0
    ) {
      setMessage({
        error:
          "Minimum notice cannot be less than 0 hours.",
        success: "",
      });

      return;
    }

    try {
      setIsSaving(true);
      setMessage(createEmptyMessage());

      await axios.put(
        `${ApiConnectionString}/api/bookingRules`,
        {
          daysInAdvance,
          minimumNoticeRequired,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setMessage({
        error: "",
        success: "Booking rules saved successfully.",
      });
    } catch (error) {
      console.error(error);

      setMessage({
        error: getErrorMessage(
          error,
          "Failed to save booking rules."
        ),
        success: "",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsCard
      icon={<Clock />}
      title="Booking rules"
      description="Control how far ahead clients can book and the notice required."
    >
      <div className="settings-form">
        {isLoading ? (
          <div className="availability-settings-loading">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            <SectionMessage message={message} />

            <div className="booking-rule-row">
              <label htmlFor="days-in-advance">
                Clients can book up to
              </label>

              <input
                id="days-in-advance"
                type="number"
                min="1"
                value={bookingRules.daysInAdvance}
                onChange={(event) =>
                  updateField(
                    "daysInAdvance",
                    event.target.value
                  )
                }
                placeholder="30"
              />

              <span>days in advance</span>
            </div>

            <div className="booking-rule-row">
              <label htmlFor="minimum-notice-required">
                Minimum notice required
              </label>

              <input
                id="minimum-notice-required"
                type="number"
                min="0"
                value={
                  bookingRules.minimumNoticeRequired
                }
                onChange={(event) =>
                  updateField(
                    "minimumNoticeRequired",
                    event.target.value
                  )
                }
                placeholder="24"
              />

              <span>hours</span>
            </div>

            <div className="settings-actions">
              <button
                type="button"
                onClick={saveBookingRules}
                disabled={isSaving}
              >
                {isSaving
                  ? "Saving..."
                  : "Save booking rules"}
              </button>
            </div>
          </>
        )}
      </div>
    </SettingsCard>
  );
}